// Groups on this phone: creating and joining, sending messages through the relay and fetching new ones.
// The relay at /api/relay stores scrambled messages for 30 days; the history lives on the phones.
// A friend is simply a group of two.
import type { Group, GroupMessage, Payload, RelayMessage } from "../../shared/model.ts";
import { dbAll, dbDel, dbGet, dbPut } from "../db.ts";
import { $ } from "../dom.ts";
import { pinTypeLabel, t } from "../i18n.ts";
import { lsJson, lsSet, uid } from "../util.ts";
import { decrypt, encrypt, forgetKey, groupIdFor, newKey, tokenFor, type Plain } from "./crypto.ts";
import { clubEventText, isClubType } from "./club.ts";
import { groupPushOff, groupPushOn, subId } from "../push.ts";
import { openChatId } from "./chat.ts";
import { renderGroups } from "./ui.ts";

const RELAY = "/api/relay";

export let groups: Group[] = [];

/** You, as other members see you. */
export const me: { id: string; name: string } = lsJson("me", { id: "", name: "" });
if (!me.id) { me.id = uid(); saveMe(); }
export function saveMe(): void { lsSet("me", JSON.stringify(me)); }

export async function loadGroups(): Promise<void> {
  try { groups = await dbAll("groups"); } catch (e) { console.warn("Could not read groups", e); }
}
export const saveGroup = (g: Group): Promise<void> => dbPut("groups", g);
/** A group's messages on this phone, oldest first. */
export async function groupMessages(groupId: string): Promise<GroupMessage[]> {
  return (await dbAll("messages")).filter((m) => m.groupId === groupId).sort((a, b) => a.ts - b.ts);
}

/* ---------- invite links: https://…/#join=<id>.<key>.<name> ---------- */
/** The app's own address (…/app/) with the group in the # part. */
export const inviteLink = (g: Group): string => `${new URL(".", location.href).href}#join=${g.id}.${g.key}.${encodeURIComponent(g.name)}`;
export interface Invite { id: string; key: string; name: string }
export function parseInvite(text: string): Invite | null {
  const m = String(text).match(/#join=([A-Za-z0-9_-]{16,64})\.([A-Za-z0-9_-]{43})\.?(.*)$/);
  if (!m) return null;
  let name = "";
  try { name = decodeURIComponent(m[3] || ""); } catch { /* damaged name: leave it out */ }
  return { id: m[1], key: m[2], name: name.slice(0, 60) };
}

const newGroup = (id: string, key: string, token: string, name: string): Group =>
  ({ id, key, token, name, createdAt: Date.now(), lastTs: 0, unread: 0, lastText: "", lastAt: 0 });

export async function createGroup(name: string): Promise<Group> {
  const key = newKey(), token = await tokenFor(key);
  const g = newGroup(await groupIdFor(token), key, token, name);
  await saveGroup(g);
  groups.push(g);
  // the invite opens straight away; the "joined" note goes to the relay in the background
  send(g, { type: "join" }).then(() => renderGroups()).catch(() => {});
  groupPushOn(g).catch(() => {});
  return g;
}
export async function joinGroup(inv: Invite): Promise<Group> {
  let g = groups.find((x) => x.id === inv.id);
  if (!g) {
    const token = await tokenFor(inv.key);
    if (await groupIdFor(token) !== inv.id) throw new Error("bad link"); // a damaged or edited link
    g = newGroup(inv.id, inv.key, token, inv.name || t("groupUnnamed"));
    await saveGroup(g);
    groups.push(g);
    await send(g, { type: "join" });
    groupPushOn(g).catch(() => {});
  }
  return g;
}
/** Leaves a group: removes it and its messages from this phone. */
export async function leaveGroup(g: Group): Promise<void> {
  groups = groups.filter((x) => x.id !== g.id); // first, so a running sync stops saving it
  forgetKey(g.id);
  void groupPushOff(g);
  for (const m of await groupMessages(g.id)) await dbDel("messages", m.id);
  await dbDel("groups", g.id);
}

/* ---------- sending and syncing ---------- */
/** Encrypts and sends a message. `quiet`: no push notification (likes). */
export async function send(g: Group, payload: Payload, opts: { quiet?: boolean } = {}): Promise<void> {
  const msg: Plain = { ...payload, from: me.id, name: me.name || t("groupSomeone"), sentAt: Date.now() };
  const body = JSON.stringify(await encrypt(g, msg));
  const headers: Record<string, string> = { "content-type": "application/json", "x-group-token": g.token };
  if (subId) headers["x-sub-id"] = subId; // this phone's push subscription, so the relay does not wake the sender
  if (opts.quiet) headers["x-quiet"] = "1";
  const res = await fetch(`${RELAY}?g=${g.id}`, { method: "POST", headers, body, signal: AbortSignal.timeout(30000) });
  if (!res.ok) throw new Error(res.status === 413 ? "too big" : "send " + res.status);
  const { id, ts } = (await res.json()) as { id: string; ts: number };
  if (!groups.includes(g)) return; // left while sending
  await dbPut("messages", { ...msg, id, ts, groupId: g.id });
  noteLast(g, msg, ts);
  await saveGroup(g);
}

/** One line about a message, for the group list and notifications. */
export function preview(m: Plain): string {
  if (isClubType(m.type)) return clubEventText(m) ?? "";
  switch (m.type) {
    case "text": return m.text;
    case "photo": return m.text || t("msgPhoto");
    case "catch": return t("msgCatch", m.catch?.species ?? "");
    case "pin": return t("msgPin", m.pin?.name || pinTypeLabel(m.pin?.type ?? "other"));
    case "join": return t("msgJoined", m.name);
    case "comment": return t("msgComment", m.text);
    default: return "";
  }
}
function noteLast(g: Group, m: Plain, ts: number): void {
  if (ts < (g.lastAt || 0) || m.type === "like" || m.type === "clubstate") return;
  g.lastAt = ts;
  g.lastText = (m.type === "join" ? "" : (m.from === me.id ? t("msgYou") : m.name) + ": ") + preview(m);
}

const syncing = new Set<string>();
/** Fetches and decrypts a group's new messages. Returns how many were added. */
export async function syncGroup(g: Group): Promise<number> {
  if (syncing.has(g.id) || !navigator.onLine) return 0;
  syncing.add(g.id);
  let added = 0;
  try {
    for (let more = true; more;) {
      const res = await fetch(`${RELAY}?g=${g.id}&since=${g.lastTs || 0}`, { headers: { "x-group-token": g.token }, signal: AbortSignal.timeout(20000) });
      if (!res.ok) { g.broken = res.status === 403; break; }
      const data = (await res.json()) as { messages: RelayMessage[]; more: boolean };
      for (const m of data.messages) {
        g.lastTs = Math.max(g.lastTs || 0, m.ts);
        if (!groups.includes(g)) break;
        if (await dbGet("messages", m.id)) continue;
        try {
          const p = await decrypt(g, m);
          await dbPut("messages", { ...p, id: m.id, ts: m.ts, groupId: g.id });
          noteLast(g, p, m.ts);
          const counts = p.from !== me.id && !["join", "clubstate", "like"].includes(p.type) && openChatId !== g.id;
          if (counts) g.unread = (g.unread || 0) + 1;
          added++;
        } catch { /* not readable with this key: ignore */ }
      }
      more = data.more && data.messages.length > 0;
    }
    if (groups.includes(g)) await saveGroup(g); // not if the group was left while this check ran
  } catch (e) { console.warn("Sync failed", e); }
  finally { syncing.delete(g.id); }
  return added;
}
export async function syncAll(): Promise<void> {
  let added = 0;
  for (const g of groups) added += await syncGroup(g);
  if (added) { renderGroups(); updateBadge(); }
}

/** The unread count on the Groups tab. */
export function updateBadge(): void {
  const n = groups.reduce((s, g) => s + (g.unread || 0), 0), b = $("#groups-badge");
  b.textContent = n > 99 ? "99+" : String(n);
  b.hidden = !n;
}

let listTimer = 0;
/** Checks every group every 20 s while the app is on screen (push wakes it otherwise). */
export function startPolling(): void {
  clearInterval(listTimer);
  listTimer = window.setInterval(() => { if (document.visibilityState === "visible") void syncAll(); }, 20000);
}
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void syncAll(); });
addEventListener("online", () => void syncAll());
